/**
 * Outil Backlinks : le profil de liens entrants d'un domaine.
 *
 * **Rien ne s'interroge sans qu'on le demande.** Chaque appel à DataForSEO est facturé, et l'écran
 * le dit avant de le déclencher : l'analyse d'ensemble sur le bouton, chaque volet de détail sur
 * son onglet. Un volet déjà chargé se relit gratuitement — le serveur ne rappelle pas l'API.
 *
 * **Deux analyses se comparent.** C'est le seul apport que l'API ne fournit pas d'elle-même : elle
 * connaît l'historique des liens gagnés et perdus, mais pas celui de l'autorité ni du volume de
 * domaines référents. Les conserver permet de dire « +42 domaines depuis le 12 mars ».
 */

import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import {
  Link2, Plus, Trash2, RefreshCw, ExternalLink, AlertCircle, Pencil, ArrowLeft, Loader
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import config from '../config';
import { nomDePays, trierParNom } from '../utils/libellesInternationaux';

const API_URL = config.API_URL;

/** Les quatre volets, leur libellé et ce qu'ils coûtent à l'ouverture. */
const VOLETS = [
  { cle: 'liens', titre: 'Liens', filtrable: true },
  { cle: 'liensGagnes', titre: 'Liens gagnés', filtrable: true },
  { cle: 'liensPerdus', titre: 'Liens perdus', filtrable: false },
  { cle: 'domainesReferents', titre: 'Domaines référents', filtrable: true },
  { cle: 'ancres', titre: 'Ancres', filtrable: true },
  { cle: 'concurrents', titre: 'Concurrents', filtrable: false }
];

/**
 * Les seuils de spam, et le nom que DataForSEO donne à chaque bande.
 *
 * Son centre d'aide définit trois catégories : 0–30 % faible, 31–60 % moyen, 61–100 % élevé. Les
 * seuils sont leurs frontières, et le bouton porte le nom de la catégorie plutôt qu'un nombre nu —
 * « ≥ 61 » ne dit rien, « élevé » situe.
 */
const SEUILS_SPAM = [
  { valeur: 31, libelle: 'moyen' },
  { valeur: 61, libelle: 'élevé' }
];

/** La bande à laquelle appartient un score, selon DataForSEO. */
const bandeDeSpam = (score) => {
  if (score === null || score === undefined) return null;
  if (score <= 30) return 'faible';
  if (score <= 60) return 'moyen';
  return 'élevé';
};

/**
 * Lit un volet stocké, quelle que soit sa forme, pour un seuil donné.
 *
 * Trois formes coexistent côté serveur — un tableau nu, `{ lignes, total }`, puis la carte par
 * seuil. Les deux premières correspondent à des relevés antérieurs, déjà payés : l'écran doit
 * savoir les afficher plutôt que de les traiter comme absents et les refacturer.
 */
const lireVolet = (stocke, seuil) => {
  if (!stocke) return null;
  const cle = seuil ? String(seuil) : 'tous';
  if (Array.isArray(stocke)) {
    return cle === 'tous' ? { lignes: stocke, total: stocke.length } : null;
  }
  if (Array.isArray(stocke.lignes)) {
    return cle === 'tous' ? stocke : null;
  }
  return stocke[cle] || null;
};

/**
 * Ce que vaut un score d'autorité, sur l'échelle 0–100 de DataForSEO.
 *
 * Un nombre nu ne dit rien : « 30 » n'est ni bon ni mauvais tant qu'on ne sait pas contre quoi le
 * lire. Ces repères sont des ordres de grandeur, pas des seuils — l'autorité utile est toujours
 * relative aux concurrents du domaine, ce que le volet Concurrents permet justement de regarder.
 */
const REPERES_AUTORITE = [
  { jusqua: 20, texte: 'profil de liens encore mince' },
  { jusqua: 40, texte: 'profil courant pour un site établi ou local' },
  { jusqua: 60, texte: 'profil solide' },
  { jusqua: 101, texte: 'profil fort' }
];

const repereAutorite = (rank) => {
  if (rank === null || rank === undefined) return null;
  return (REPERES_AUTORITE.find(r => rank < r.jusqua) || {}).texte || null;
};

const entier = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 });

/** Une valeur absente rend une cellule vide, jamais « 0 » — la règle de tout le produit. */
const nombre = (v) => (v === null || v === undefined ? '—' : entier.format(v));

/** L'écart entre deux analyses, avec son signe. Rien si l'une des deux manque. */
const ecart = (actuel, precedent) => {
  if (actuel === null || actuel === undefined || precedent === null || precedent === undefined) return null;
  const d = actuel - precedent;
  if (d === 0) return null;
  return `${d > 0 ? '+' : ''}${entier.format(d)}`;
};

/**
 * Une date au jour, en français.
 *
 * **Sans passer par `Date`** quand la chaîne commence par `AAAA-MM-JJ`, ce qui est le cas de tout
 * ce que rend DataForSEO (`2026-08-29 14:13:07 +00:00`). Ce format, avec une espace au lieu du
 * « T », n'est pas de l'ISO valide : V8 l'accepte, Safari le refuse et rendrait « Invalid Date ».
 * Comme on n'affiche que le jour, le découpage est à la fois plus sûr et plus juste — il évite
 * aussi le décalage de fuseau qui peut faire reculer une date d'un jour en soirée.
 */
const dateCourte = (valeur) => {
  if (!valeur) return '—';
  const jour = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(valeur));
  if (jour) return `${jour[3]}/${jour[2]}/${jour[1]}`;
  const d = new Date(valeur);
  return isNaN(d) ? '—' : d.toLocaleDateString('fr-FR');
};

const styles = {
  carte: { background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '20px' },
  bouton: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 500 },
  principal: { background: '#7c2d12', color: '#fff' },
  secondaire: { background: '#f3f4f6', color: '#374151' },
  champ: { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', boxSizing: 'border-box' }
};

function BacklinksTool({ token, selectedClientId }) {
  const [projets, setProjets] = useState([]);
  const [clients, setClients] = useState([]);
  const [projetOuvert, setProjetOuvert] = useState(null);
  const [analyse, setAnalyse] = useState(null);
  const [historique, setHistorique] = useState([]);
  const [voletActif, setVoletActif] = useState('liens');
  // `null` signifie « tous ». Le seuil est un état d'écran et non une préférence conservée : il se
  // remet à zéro d'un projet à l'autre, comme le volet actif.
  const [seuilSpam, setSeuilSpam] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [analyseEnCours, setAnalyseEnCours] = useState(false);
  const [voletEnCours, setVoletEnCours] = useState(null);
  const [erreur, setErreur] = useState('');

  const [modaleNouveau, setModaleNouveau] = useState(false);
  const [nouveau, setNouveau] = useState({ name: '', domain: '', clientId: '' });
  const [projetAEditer, setProjetAEditer] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };
  const base = `${API_URL}/api/tools/backlinks`;

  const chargerProjets = useCallback(async () => {
    try {
      setErreur('');
      const params = selectedClientId ? `?client=${selectedClientId}` : '';
      const r = await axios.get(`${base}/projects${params}`, { headers });
      setProjets(r.data.data || []);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Impossible de charger les projets');
    }
  }, [selectedClientId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const chargerClients = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/client-management/clients`, { headers });
      setClients(r.data || []);
    } catch (e) {
      // Le sélecteur de client n'est qu'un confort : son absence ne doit pas bloquer l'outil.
      console.error('Erreur chargement clients :', e);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { chargerProjets(); chargerClients(); }, [chargerProjets, chargerClients]);

  /** Ouvre un projet : son historique, et sa dernière analyse s'il en a une. */
  const ouvrir = async (projet) => {
    setProjetOuvert(projet);
    setAnalyse(null);
    setVoletActif('liens');
    setSeuilSpam(null);
    setChargement(true);
    setErreur('');
    try {
      const r = await axios.get(`${base}/projects/${projet.id}/analyses`, { headers });
      const liste = r.data.data || [];
      setHistorique(liste);
      if (liste[0]) {
        const detail = await axios.get(`${base}/analyses/${liste[0].id}`, { headers });
        setAnalyse(detail.data.data);
      }
    } catch (e) {
      setErreur(e.response?.data?.error || 'Impossible de charger les analyses');
    } finally {
      setChargement(false);
    }
  };

  const analyser = async () => {
    setAnalyseEnCours(true);
    setErreur('');
    try {
      const r = await axios.post(`${base}/projects/${projetOuvert.id}/analyser`, {}, { headers });
      setAnalyse(r.data.data);
      const h = await axios.get(`${base}/projects/${projetOuvert.id}/analyses`, { headers });
      setHistorique(h.data.data || []);
      chargerProjets();
    } catch (e) {
      setErreur(e.response?.data?.error || 'L\'analyse a échoué');
    } finally {
      setAnalyseEnCours(false);
    }
  };

  /** Charge un volet, ou le relit s'il est déjà là. Le serveur tranche, pas l'écran. */
  /**
   * Ouvre un volet à un seuil de spam donné, et ne l'interroge que si on ne l'a pas déjà.
   *
   * Le seuil fait partie de l'identité de ce qu'on charge : « Liens » et « Liens au spam ≥ 50 »
   * sont deux jeux de résultats distincts, et le serveur les met en cache séparément.
   */
  const chargerVolet = async (cle, seuil = seuilSpam) => {
    setVoletActif(cle);
    setSeuilSpam(seuil);

    const filtrable = VOLETS.find(v => v.cle === cle)?.filtrable;
    // Un volet sans score de spam ignore le seuil : le serveur le refuserait, et l'onglet doit
    // rester ouvrable sans qu'on ait à remettre le filtre à « tous » d'abord.
    const seuilEffectif = filtrable ? seuil : null;

    if (!analyse || lireVolet(analyse[cle], seuilEffectif)) return;

    setVoletEnCours(cle);
    setErreur('');
    try {
      const parametres = seuilEffectif ? `?spam=${seuilEffectif}` : '';
      const r = await axios.post(`${base}/analyses/${analyse.id}/volet/${cle}${parametres}`, {}, { headers });
      // On fusionne dans la carte locale pour la même raison que le serveur : ne pas faire oublier
      // les seuils déjà chargés, dont la relecture est gratuite.
      setAnalyse(a => {
        const ancien = a[cle];
        const carte = (ancien && !Array.isArray(ancien) && !Array.isArray(ancien.lignes))
          ? { ...ancien }
          : (ancien ? { tous: lireVolet(ancien, null) } : {});
        carte[seuilEffectif ? String(seuilEffectif) : 'tous'] = r.data.data;
        return { ...a, [cle]: carte };
      });
    } catch (e) {
      setErreur(e.response?.data?.error || `Le volet « ${cle} » n'a pas pu être chargé`);
    } finally {
      setVoletEnCours(null);
    }
  };

  const creer = async () => {
    if (!nouveau.name || !nouveau.domain) return;
    try {
      await axios.post(`${base}/projects`, nouveau, { headers });
      setModaleNouveau(false);
      setNouveau({ name: '', domain: '', clientId: '' });
      chargerProjets();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Création impossible');
    }
  };

  const enregistrerEdition = async () => {
    try {
      await axios.put(`${base}/projects/${projetAEditer.id}`, projetAEditer, { headers });
      setProjetAEditer(null);
      chargerProjets();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Modification impossible');
    }
  };

  const supprimer = async (projet) => {
    // La confirmation nomme le projet et dit ce qui disparaît : depuis une grille de cartes,
    // « ce projet » ne désigne rien, et une suppression définitive doit s'annoncer comme telle.
    const analyses = projet.nbAnalyses
      ? `\n\nSes ${projet.nbAnalyses} analyse(s) conservée(s) seront perdues, ainsi que l'historique de comparaison.`
      : '';
    if (!window.confirm(`Supprimer définitivement « ${projet.name} » ?${analyses}`)) return;

    try {
      await axios.delete(`${base}/projects/${projet.id}`, { headers });
      if (projetOuvert?.id === projet.id) setProjetOuvert(null);
      chargerProjets();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Suppression impossible');
    }
  };

  const bandeauErreur = erreur && (
    <div style={{ ...styles.carte, background: '#fef2f2', display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '14px 16px' }}>
      <AlertCircle size={18} style={{ color: '#b91c1c', flexShrink: 0, marginTop: '1px' }} />
      <span style={{ fontSize: '0.875rem', color: '#b91c1c' }}>{erreur}</span>
    </div>
  );

  // ==================== LISTE DES PROJETS ====================

  if (!projetOuvert) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '9px' }}>
              <Link2 size={22} /> Backlinks
            </h2>
            <p style={{ margin: '6px 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
              Le profil de liens entrants d'un domaine : qui vous cite, avec quelle autorité, ce que
              vous gagnez et ce que vous perdez. Chaque analyse est conservée et se compare aux
              précédentes.
            </p>
          </div>
          <button onClick={() => setModaleNouveau(true)} style={{ ...styles.bouton, ...styles.principal }}>
            <Plus size={16} /> Nouveau projet
          </button>
        </div>

        {bandeauErreur}

        {projets.length === 0 ? (
          <div style={{ ...styles.carte, textAlign: 'center', color: '#6b7280', padding: '48px 20px' }}>
            <Link2 size={32} style={{ opacity: 0.4 }} />
            <p style={{ marginTop: '12px' }}>Aucun domaine suivi. Déclarez-en un pour analyser son profil de liens.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
            {projets.map(p => (
              <div
                key={p.id}
                onClick={() => ouvrir(p)}
                style={{ ...styles.carte, marginBottom: 0, cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ minWidth: 0 }}>
                    <h3 style={{ margin: 0, fontSize: '1rem' }}>{p.name}</h3>
                    <p style={{ margin: '3px 0 0', fontSize: '0.8125rem', color: '#6b7280' }}>{p.domain}</p>
                    {p.clientName && (
                      <span style={{ fontSize: '0.75rem', color: '#7c2d12', background: '#fef3c7', padding: '2px 7px', borderRadius: '4px', display: 'inline-block', marginTop: '6px' }}>
                        {p.clientName}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); setProjetAEditer({ ...p }); }}
                      title="modifier ce projet"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#9ca3af' }}
                    >
                      <Pencil size={15} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); supprimer(p); }}
                      title="supprimer ce projet"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#ef4444' }}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>

                {p.derniereAnalyse ? (
                  <div style={{ marginTop: '14px', display: 'flex', gap: '18px', fontSize: '0.8125rem' }}>
                    <span><strong>{nombre(p.derniereAnalyse.rank)}</strong> <span style={{ color: '#6b7280' }}>autorité</span></span>
                    <span><strong>{nombre(p.derniereAnalyse.backlinks)}</strong> <span style={{ color: '#6b7280' }}>liens</span></span>
                    <span><strong>{nombre(p.derniereAnalyse.referringDomains)}</strong> <span style={{ color: '#6b7280' }}>domaines</span></span>
                  </div>
                ) : (
                  <p style={{ marginTop: '14px', fontSize: '0.8125rem', color: '#9ca3af' }}>Jamais analysé</p>
                )}
              </div>
            ))}
          </div>
        )}

        {modaleNouveau && ReactDOM.createPortal(
          <div onClick={() => setModaleNouveau(false)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '14px', padding: '24px', width: '90%', maxWidth: '460px' }}>
              <h3 style={{ marginTop: 0 }}>Nouveau projet</h3>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>Nom</label>
              <input value={nouveau.name} onChange={e => setNouveau({ ...nouveau, name: e.target.value })} placeholder="Mon site" style={{ ...styles.champ, marginBottom: '14px' }} />
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>Domaine</label>
              <input value={nouveau.domain} onChange={e => setNouveau({ ...nouveau, domain: e.target.value })} placeholder="exemple.fr" style={{ ...styles.champ, marginBottom: '4px' }} />
              <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 14px' }}>
                Le protocole et le « www. » sont retirés automatiquement.
              </p>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>Client</label>
              <select value={nouveau.clientId} onChange={e => setNouveau({ ...nouveau, clientId: e.target.value })} style={{ ...styles.champ, marginBottom: '18px' }}>
                <option value="">Aucun client</option>
                {trierParNom(clients).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button onClick={() => setModaleNouveau(false)} style={{ ...styles.bouton, ...styles.secondaire }}>Annuler</button>
                <button onClick={creer} disabled={!nouveau.name || !nouveau.domain} style={{ ...styles.bouton, ...styles.principal, opacity: (!nouveau.name || !nouveau.domain) ? 0.5 : 1 }}>Créer</button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {projetAEditer && ReactDOM.createPortal(
          <div onClick={() => setProjetAEditer(null)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '14px', padding: '24px', width: '90%', maxWidth: '460px' }}>
              <h3 style={{ marginTop: 0 }}>Modifier le projet</h3>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>Nom</label>
              <input value={projetAEditer.name} onChange={e => setProjetAEditer({ ...projetAEditer, name: e.target.value })} style={{ ...styles.champ, marginBottom: '14px' }} />
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>Domaine</label>
              <input value={projetAEditer.domain} onChange={e => setProjetAEditer({ ...projetAEditer, domain: e.target.value })} style={{ ...styles.champ, marginBottom: '14px' }} />
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>Client</label>
              <select value={projetAEditer.clientId || ''} onChange={e => setProjetAEditer({ ...projetAEditer, clientId: e.target.value })} style={{ ...styles.champ, marginBottom: '18px' }}>
                <option value="">Aucun client</option>
                {trierParNom(clients).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button onClick={() => setProjetAEditer(null)} style={{ ...styles.bouton, ...styles.secondaire }}>Annuler</button>
                <button onClick={enregistrerEdition} style={{ ...styles.bouton, ...styles.principal }}>Enregistrer</button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  }

  // ==================== VUE DÉTAILLÉE ====================

  const precedente = historique[1] || null;
  const voletCourantFiltrable = VOLETS.find(v => v.cle === voletActif)?.filtrable;
  const seuilCourant = voletCourantFiltrable ? seuilSpam : null;

  // On lit les lignes *et* le total : afficher cent lignes sans dire qu'il en existe quatre mille
  // laisserait croire le profil bien plus petit qu'il n'est.
  const volet = lireVolet(analyse?.[voletActif], seuilCourant);
  const lignesDuVolet = volet?.lignes || null;
  const totalDuVolet = volet?.total ?? null;

  /** Un volet, à un seuil donné, a-t-il déjà été payé ? */
  const dejaCharge = (cle, seuil) => {
    const filtrable = VOLETS.find(v => v.cle === cle)?.filtrable;
    return Boolean(lireVolet(analyse?.[cle], filtrable ? seuil : null));
  };

  const metrique = (titre, valeur, avant, options = {}) => {
    const d = ecart(valeur, avant);
    return (
      <div style={{ flex: '1 1 140px' }} title={options.aide || undefined}>
        <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{titre}</div>
        <div style={{ fontSize: '1.5rem', fontWeight: 600, marginTop: '4px' }}>
          {nombre(valeur)}
          {/* L'échelle se lit à côté du nombre, pas dans une infobulle : sans elle, « 30 » ne se
              compare à rien — c'est exactement ce qu'un réglage sur 0–1000 avait produit. */}
          {options.surCent && valeur !== null && valeur !== undefined && valeur <= 100 && (
            <span style={{ fontSize: '0.875rem', fontWeight: 400, color: '#9ca3af' }}> / 100</span>
          )}
          {/* Un rang se lit « 30 / 100 », un pourcentage « 33 % » : ce ne sont pas les mêmes objets,
              et les afficher pareil laisserait croire à deux notes comparables. */}
          {options.pourcent && valeur !== null && valeur !== undefined && (
            <span style={{ fontSize: '0.875rem', fontWeight: 400, color: '#9ca3af' }}> %</span>
          )}
        </div>
        {options.repere && (
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '2px' }}>{options.repere}</div>
        )}
        {/* Une valeur hors échelle vient d'une analyse antérieure au passage sur 0–100. Le dire
            vaut mieux que d'afficher un nombre que rien n'explique : c'est la même règle que
            partout ailleurs dans le produit — une donnée absente ou périmée s'annonce. */}
        {options.surCent && valeur > 100 && (
          <div style={{ fontSize: '0.75rem', color: '#b45309', marginTop: '2px' }}>
            relevé sur une échelle antérieure — relancez l'analyse
          </div>
        )}
        {d && (
          <div style={{ fontSize: '0.8125rem', color: d.startsWith('+') ? '#059669' : '#b45309', marginTop: '2px' }}>
            {d} depuis le {dateCourte(precedente?.analyseLe)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={() => setProjetOuvert(null)} style={{ ...styles.bouton, ...styles.secondaire }}>
            <ArrowLeft size={16} /> Projets
          </button>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.125rem' }}>{projetOuvert.name}</h2>
            <a
              href={`https://${projetOuvert.domain}`}
              target="_blank"
              rel="nofollow noopener noreferrer"
              style={{ fontSize: '0.8125rem', color: '#6b7280', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
            >
              {projetOuvert.domain} <ExternalLink size={12} />
            </a>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {analyse && (
            <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
              Analysé le {dateCourte(analyse.analyseLe)}
            </span>
          )}
          <button onClick={analyser} disabled={analyseEnCours} style={{ ...styles.bouton, ...styles.principal, opacity: analyseEnCours ? 0.6 : 1 }}>
            {analyseEnCours ? <Loader size={16} className="spin" /> : <RefreshCw size={16} />}
            {analyse ? 'Analyser à nouveau' : 'Analyser'}
          </button>
        </div>
      </div>

      {bandeauErreur}

      {chargement ? (
        <div style={{ ...styles.carte, textAlign: 'center', padding: '48px' }}><Loader size={22} className="spin" /></div>
      ) : !analyse ? (
        <div style={{ ...styles.carte, textAlign: 'center', color: '#6b7280', padding: '44px 20px' }}>
          <p style={{ margin: 0 }}>Ce domaine n'a jamais été analysé.</p>
          <p style={{ margin: '8px 0 0', fontSize: '0.8125rem' }}>
            Une analyse interroge DataForSEO et coûte environ 0,05 $. Elle est ensuite conservée :
            la relire ne coûte rien.
          </p>
        </div>
      ) : (
        <>
          <div style={{ ...styles.carte, display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            {metrique('Autorité', analyse.rank, precedente?.rank, {
              surCent: true,
              repere: repereAutorite(analyse.rank),
              aide: "Le rang de domaine calculé par DataForSEO, sur 0 à 100 — la même échelle que "
                + "le Domain Rating d'Ahrefs ou l'Authority Score de Semrush, sans être le même "
                + "calcul. Il se lit par comparaison avec les domaines concurrents, pas dans l'absolu."
            })}
            {metrique('Backlinks', analyse.backlinks, precedente?.backlinks)}
            {metrique('Domaines référents', analyse.referringDomains, precedente?.referringDomains)}
            {metrique('dont nofollow', analyse.referringDomainsNofollow, precedente?.referringDomainsNofollow)}
            {metrique('Adresses IP', analyse.referringIps, precedente?.referringIps)}
            {metrique('Liens cassés', analyse.brokenBacklinks, precedente?.brokenBacklinks)}
            {metrique('Score de spam', analyse.spamScore, precedente?.spamScore, {
              pourcent: true,
              repere: bandeDeSpam(analyse.spamScore),
              aide: "Score de spam de DataForSEO, en pourcentage : 0–30 % faible, 31–60 % moyen, "
                + "61–100 % élevé. Il agrège 18 signaux et, pour un domaine, c'est la moyenne de ses "
                + "pages. Plusieurs signaux sont grossiers — rester en HTTP ajoute 30 %, ne pas "
                + "afficher de coordonnées 20 % — donc un site parfaitement légitime peut se "
                + "retrouver en « moyen ». À lire comme une piste, pas comme un verdict."
            })}
          </div>

          {analyse.evolution?.length > 0 && (
            <div style={styles.carte}>
              <h3 style={{ margin: '0 0 14px', fontSize: '0.9375rem' }}>Liens gagnés et perdus, par mois</h3>
              <ResponsiveContainer width="100%" height={230}>
                <LineChart data={analyse.evolution.map(m => ({ ...m, mois: String(m.date).slice(0, 7) }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="mois" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="nouveaux" name="Gagnés" stroke="#059669" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="perdus" name="Perdus" stroke="#dc2626" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
              <p style={{ margin: '10px 0 0', fontSize: '0.8125rem', color: '#6b7280' }}>
                Cette courbe donne les volumes. Pour savoir <em>quels</em> liens, ouvrez les onglets
                Liens gagnés et Liens perdus ci-dessous.
              </p>
            </div>
          )}

          <div style={styles.carte}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
              {VOLETS.map(v => (
                <button
                  key={v.cle}
                  onClick={() => chargerVolet(v.cle)}
                  // Le coût n'est annoncé que si ce volet, à ce seuil, n'a pas encore été chargé :
                  // une action facturée ne doit pas se déclencher par surprise, mais le rappeler sur
                  // un résultat déjà payé induirait en erreur.
                  title={dejaCharge(v.cle, seuilSpam) ? 'Déjà chargé' : 'Interroge DataForSEO (~0,03 $)'}
                  style={{
                    ...styles.bouton,
                    padding: '7px 14px',
                    background: voletActif === v.cle ? '#7c2d12' : '#fff',
                    color: voletActif === v.cle ? '#fff' : '#374151',
                    border: '1px solid ' + (voletActif === v.cle ? '#7c2d12' : '#e5e7eb')
                  }}
                >
                  {v.titre}
                  {!dejaCharge(v.cle, seuilSpam) && <span style={{ opacity: 0.7, fontSize: '11px' }}>~0,03 $</span>}
                </button>
              ))}
            </div>

            {/*
              Le filtre de spam part à l'API, il ne trie pas les lignes affichées.
              C'est le point essentiel : les cent lignes qu'on tient sont les plus autoritaires, et
              les liens toxiques sont justement ceux qui n'y sont pas. Un filtre local aurait donc
              annoncé un profil sain en n'ayant simplement pas regardé au bon endroit.
            */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
              marginBottom: '16px', paddingBottom: '14px', borderBottom: '1px solid #f1f5f9'
            }}>
              <span style={{ fontSize: '0.8125rem', color: '#374151', fontWeight: 500 }}>Score de spam</span>
              {[null, ...SEUILS_SPAM.map(b => b.valeur)].map(seuil => {
                const actif = seuilCourant === seuil;
                const gratuit = dejaCharge(voletActif, seuil);
                const bande = SEUILS_SPAM.find(b => b.valeur === seuil);
                return (
                  <button
                    key={seuil === null ? 'tous' : seuil}
                    onClick={() => chargerVolet(voletActif, seuil)}
                    disabled={!voletCourantFiltrable && seuil !== null}
                    title={
                      !voletCourantFiltrable && seuil !== null
                        ? 'Ce volet ne porte pas de score de spam'
                        : gratuit ? 'Déjà chargé' : 'Interroge DataForSEO (~0,03 $)'
                    }
                    style={{
                      ...styles.bouton, padding: '5px 11px', fontSize: '0.8125rem',
                      background: actif ? '#fef3c7' : '#fff',
                      color: (!voletCourantFiltrable && seuil !== null) ? '#d1d5db' : (actif ? '#7c2d12' : '#374151'),
                      border: '1px solid ' + (actif ? '#f59e0b' : '#e5e7eb'),
                      cursor: (!voletCourantFiltrable && seuil !== null) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {seuil === null ? 'tous' : `${bande.libelle} et +`}
                    {seuil !== null && (
                      <span style={{ opacity: 0.55, fontSize: '10px' }}>≥ {seuil} %</span>
                    )}
                    {voletCourantFiltrable && !gratuit && seuil !== null && (
                      <span style={{ opacity: 0.6, fontSize: '10px' }}>~0,03 $</span>
                    )}
                  </button>
                );
              })}
              {!voletCourantFiltrable && (
                <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                  {voletActif === 'concurrents'
                    ? 'les concurrents ne portent pas de score de spam'
                    : 'un lien perdu de mauvaise qualité n\'est pas un problème à isoler'}
                </span>
              )}
            </div>

            {voletEnCours === voletActif ? (
              <div style={{ textAlign: 'center', padding: '32px' }}><Loader size={20} className="spin" /></div>
            ) : !volet ? (
              <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>
                {seuilCourant
                  ? `Ce volet n'a pas encore été chargé pour un spam ≥ ${seuilCourant} %. Cliquez sur ce seuil pour l'interroger.`
                  : "Ce volet n'a pas encore été chargé. Cliquez sur son onglet pour l'interroger."}
              </p>
            ) : lignesDuVolet.length === 0 ? (
              <p style={{ color: seuilCourant ? '#059669' : '#6b7280', fontSize: '0.875rem', margin: 0 }}>
                {/* Un filtre qui ne rend rien est une bonne nouvelle, pas une absence de données :
                    le dire en vert évite de le lire comme un relevé qui a échoué. */}
                {seuilCourant
                  ? `Aucune ligne au score de spam ≥ ${seuilCourant} % — rien à ce niveau.`
                  : voletActif === 'liensPerdus'
                    ? 'Aucun lien perdu — rien n\'a disparu du profil.'
                    : 'Aucune donnée pour ce volet.'}
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                {/*
                  Le volet Concurrents demande une explication que son tableau ne peut pas porter :
                  ces domaines ne sont pas des concurrents commerciaux mais des sites que vos
                  propres sources citent aussi. Quand un profil est dominé par des annuaires, on
                  obtient donc d'autres annuaires — ce qui se lit comme une erreur si personne ne
                  l'a dit.
                */}
                {voletActif === 'concurrents' && (
                  <p style={{
                    margin: '0 0 12px', padding: '10px 12px', background: '#faf7f5',
                    borderRadius: '8px', fontSize: '0.8125rem', color: '#6b7280', lineHeight: 1.5
                  }}>
                    Des sites que vos propres sources de liens citent également — pas des concurrents
                    commerciaux. C'est ce qui sert à repérer un lien qu'on pourrait obtenir : si une
                    page vous cite tous les deux, elle est atteignable. En revanche, si votre profil
                    est dominé par des annuaires, cette liste en rendra d'autres.{' '}
                    <span title={
                      "La documentation de DataForSEO dit seulement « nombre d'intersections de "
                      + "backlinks », sans préciser ce qui est compté. Sur un cas mesuré, 35 domaines "
                      + "référents étaient réellement partagés là où l'API annonçait 8 : la colonne "
                      + "classe donc les concurrents du plus proche au plus lointain, mais son "
                      + "nombre ne se lit pas comme un décompte."
                    } style={{ borderBottom: '1px dotted #9ca3af', cursor: 'help' }}>
                      La colonne Intersections ordonne, elle ne compte pas.
                    </span>
                  </p>
                )}
                {/* Une liste tronquée doit le dire, et préciser sur quoi porte le décompte : « 100
                    sur 57 au spam ≥ 50 » ne veut pas dire la même chose que « 100 sur 4 312 ». */}
                <p style={{ margin: '0 0 10px', fontSize: '0.8125rem', color: '#6b7280' }}>
                  {totalDuVolet !== null && totalDuVolet > lignesDuVolet.length
                    ? `${entier.format(lignesDuVolet.length)} ligne(s) affichée(s) sur ${entier.format(totalDuVolet)}`
                    : `${entier.format(lignesDuVolet.length)} ligne(s)`}
                  {seuilCourant && ` au score de spam ≥ ${seuilCourant} % (${bandeDeSpam(seuilCourant)} et au-delà)`}
                </p>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                  <thead>
                    <tr style={{ background: '#faf7f5' }}>
                      {Object.keys(lignesDuVolet[0]).map(c => (
                        <th key={c} style={{ textAlign: 'left', padding: '8px 10px', color: '#7c2d12', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {ENTETES[c] || c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {lignesDuVolet.map((ligne, i) => (
                      <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                        {Object.entries(ligne).map(([c, v]) => (
                          <td key={c} style={{ padding: '6px 10px', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {cellule(c, v)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {historique.length > 1 && (
            <div style={styles.carte}>
              <h3 style={{ margin: '0 0 12px', fontSize: '0.9375rem' }}>Analyses précédentes</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                <thead>
                  <tr style={{ background: '#faf7f5' }}>
                    {['Date', 'Autorité', 'Backlinks', 'Domaines', 'Spam', 'Coût'].map(t => (
                      <th key={t} style={{ textAlign: 'left', padding: '8px 10px', color: '#7c2d12', fontWeight: 600 }}>{t}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {historique.map(a => (
                    <tr key={a.id} style={{ borderTop: '1px solid #f1f5f9', background: a.id === analyse.id ? '#fffbf5' : 'transparent' }}>
                      <td style={{ padding: '6px 10px' }}>{dateCourte(a.analyseLe)}</td>
                      <td style={{ padding: '6px 10px' }}>{nombre(a.rank)}</td>
                      <td style={{ padding: '6px 10px' }}>{nombre(a.backlinks)}</td>
                      <td style={{ padding: '6px 10px' }}>{nombre(a.referringDomains)}</td>
                      <td style={{ padding: '6px 10px' }}>{nombre(a.spamScore)}</td>
                      <td style={{ padding: '6px 10px', color: '#6b7280' }}>{a.cout ? `${a.cout.toFixed(3)} $` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Libellés des colonnes des volets, dont les clés viennent du service. */
const ENTETES = {
  domaine: 'Domaine', urlSource: 'Page source', urlCible: 'Page visée', ancre: 'Ancre',
  dofollow: 'Dofollow', autorite: 'Autorité', spam: 'Spam', pays: 'Pays',
  premiereVue: 'Vu le', derniereVue: 'Revu le', casse: 'Cassé',
  apparuLe: 'Apparu le', disparuLe: 'Disparu le',
  backlinks: 'Liens', pages: 'Pages', domaines: 'Domaines',
  intersections: 'Intersections',
  // Ancien nom du même champ, conservé pour que les analyses déjà enregistrées gardent un en-tête
  // lisible plutôt que d'afficher la clé brute.
  sourcesCommunes: 'Intersections'
};

/**
 * Rendu d'une cellule selon sa colonne.
 *
 * Les dates de DataForSEO arrivent avec leur heure et leur fuseau ; les afficher telles quelles
 * remplirait la moitié du tableau pour une information qu'on lit au jour près.
 */
function cellule(colonne, valeur) {
  if (valeur === null || valeur === undefined) return '—';
  if (typeof valeur === 'boolean') return valeur ? 'oui' : 'non';
  if (colonne === 'pays') return nomDePays(valeur, valeur);
  if (['premiereVue', 'derniereVue', 'apparuLe', 'disparuLe'].includes(colonne)) return dateCourte(valeur);
  if (colonne === 'urlSource' || colonne === 'urlCible') {
    return (
      <a href={valeur} target="_blank" rel="nofollow noopener noreferrer" title={valeur} style={{ color: '#6b7280', textDecoration: 'none' }}>
        {String(valeur).replace(/^https?:\/\//, '').slice(0, 60)}
      </a>
    );
  }
  if (typeof valeur === 'number') return entier.format(valeur);
  return String(valeur);
}

export default BacklinksTool;
